import { computed, ref, watch, onBeforeUnmount } from 'vue';
import { useEventListener, useResizeObserver } from '@vueuse/core';

const DEFAULT_HEIGHT = 120;
const MIN_HEIGHT = 80;
const MIN_MESSAGES_HEIGHT = 200;
const EXPAND_RATIO = 0.5;
const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

export function useResizableEditor({
  containerHeight,
  wrapperRef,
  editorSelector,
  collapsible,
  onCollapse,
}) {
  const surroundingHeight = ref(0);
  const editorHeight = ref(DEFAULT_HEIGHT);
  const requestedHeight = ref(0);
  const isResizing = ref(false);
  let startY = 0;
  let startHeight = 0;

  const measureSurroundingHeight = () => {
    const wrapper = wrapperRef.value;
    const editor = wrapper?.querySelector(editorSelector.value);
    if (editor)
      surroundingHeight.value = Math.max(
        0,
        wrapper.offsetHeight - editor.offsetHeight
      );
  };

  const sizeBounds = computed(() => {
    const height = containerHeight.value;
    const max =
      height > 0
        ? Math.max(
            MIN_HEIGHT,
            height - MIN_MESSAGES_HEIGHT - surroundingHeight.value
          )
        : DEFAULT_HEIGHT;
    return {
      min: MIN_HEIGHT,
      max,
      expanded: clamp(
        Math.floor(height * EXPAND_RATIO - surroundingHeight.value / 2),
        MIN_HEIGHT,
        max
      ),
      default: clamp(DEFAULT_HEIGHT, MIN_HEIGHT, max),
    };
  });

  const clampToBounds = value =>
    clamp(value, sizeBounds.value.min, sizeBounds.value.max);
  const appliedHeight = computed(() =>
    clampToBounds(Math.max(editorHeight.value, requestedHeight.value))
  );
  const requestEditorHeight = height => {
    measureSurroundingHeight();
    requestedHeight.value = height
      ? Math.max(height, sizeBounds.value.default)
      : 0;
  };
  const isExpanded = computed(
    () => appliedHeight.value > sizeBounds.value.default
  );
  const resetEditorHeight = () => {
    requestedHeight.value = 0;
    editorHeight.value = sizeBounds.value.default;
  };
  const resizeBy = delta => {
    measureSurroundingHeight();
    editorHeight.value = clampToBounds(appliedHeight.value + delta);
    requestedHeight.value = 0;
  };
  const toggleEditorExpand = () => {
    measureSurroundingHeight();
    const { expanded, max, default: defaultHeight } = sizeBounds.value;
    const target = expanded - defaultHeight < 100 ? max : expanded;
    editorHeight.value = isExpanded.value ? defaultHeight : target;
    requestedHeight.value = 0;
  };
  const getClientY = event =>
    event.touches ? event.touches[0].clientY : event.clientY;
  const onResizeStart = event => {
    measureSurroundingHeight();
    isResizing.value = true;
    startY = getClientY(event);
    startHeight = appliedHeight.value;
    requestedHeight.value = 0;
    editorHeight.value = startHeight;
    document.body.classList.add('cursor-row-resize', 'select-none');
  };
  const onResizeEnd = () => {
    if (!isResizing.value) return;
    isResizing.value = false;
    document.body.classList.remove('cursor-row-resize', 'select-none');
  };
  const onResizeMove = event => {
    if (!isResizing.value) return;
    if (event.touches) event.preventDefault();
    const draggedHeight = startHeight + startY - getClientY(event);
    editorHeight.value = clampToBounds(draggedHeight);
    if (collapsible?.value && draggedHeight <= sizeBounds.value.min - 48) {
      onResizeEnd();
      resetEditorHeight();
      onCollapse();
    }
  };

  watch(sizeBounds, () => {
    editorHeight.value = clampToBounds(editorHeight.value);
  });
  useResizeObserver(wrapperRef, measureSurroundingHeight);
  useEventListener(document, 'mousemove', onResizeMove);
  useEventListener(document, 'mouseup', onResizeEnd);
  useEventListener(document, 'touchmove', onResizeMove, { passive: false });
  useEventListener(document, 'touchend', onResizeEnd);
  useEventListener(document, 'touchcancel', onResizeEnd);
  useEventListener(window, 'blur', onResizeEnd);
  onBeforeUnmount(onResizeEnd);

  return {
    editorHeight,
    appliedHeight,
    requestEditorHeight,
    sizeBounds,
    isExpanded,
    isResizing,
    onResizeStart,
    resizeBy,
    resetEditorHeight,
    toggleEditorExpand,
  };
}
